/**
 * Each pick of attachments uploads once, as its own group.
 *
 * Uploading to a project registers its documents and starts paid reading at once, so sending the
 * whole tray again on every pick registered 1 + 2 + 3 + 4 + 5 = 15 documents for five PDFs added one
 * at a time (project c76e55c8, 20 Oct 2026), each charged and read separately.
 */
export interface UploadGroup {
  files: File[];
  controller: globalThis.AbortController;
  promise: Promise<string[]>;
  done: boolean;
}

/** Selected files that no live upload covers: never uploaded, or their upload failed. */
export function filesWithoutUpload(files: File[], groups: UploadGroup[]): File[] {
  return files.filter((file) => !groups.some((group) => group.files.includes(file)));
}

/** Groups that still carry a selected file; the others are aborted and dropped. */
export function keepAttachedGroups(groups: UploadGroup[], files: File[]): UploadGroup[] {
  return groups.filter((group) => {
    const attached = group.files.some((file) => files.includes(file));

    if (!attached) group.controller.abort();

    return attached;
  });
}

/** The uploaded URL of each selected file, in the order the files are selected. */
export async function uploadedUrls(files: File[], groups: UploadGroup[]): Promise<string[]> {
  const needed = groups.filter((group) => group.files.some((file) => files.includes(file)));
  const results = await Promise.all(needed.map((group) => group.promise));
  const urlByFile = new Map<File, string>();

  needed.forEach((group, index) => group.files.forEach((file, position) => {
    urlByFile.set(file, results[index][position]);
  }));

  return files.map((file) => urlByFile.get(file) as string);
}

export function isAbortError(error: unknown): boolean {
  return error instanceof globalThis.DOMException && error.name === 'AbortError';
}
